// Hand-authored engravings + scoped CSS for the /concepts/proof board
// (Proof & data storytelling). Same shape as the other concept boards: `CSS`
// is a fully-scoped stylesheet and `E` holds SVG figures whose inner geometry
// is injected verbatim. Marks use `currentColor` linework so a container's
// `color` controls ink, and `--live` for the one alive element (theme-dark on
// cream, theme-accent on the few dark chips) so the seals, arcs and ticks
// re-ink through the palette switcher.
import type { EngravingData } from "../engraving";

export const CSS = `.concepts-proof{
  --dark:var(--theme-dark);
  --accent:var(--theme-accent);
  --ground:var(--theme-ground);
  --ink:var(--theme-ink);
  --hair:      color-mix(in srgb, var(--theme-ink) 16%, var(--theme-ground));
  --hair-soft: color-mix(in srgb, var(--theme-ink)  9%, var(--theme-ground));
  --dot:       color-mix(in srgb, var(--theme-ink)  6%, transparent);
  --ink-88: color-mix(in srgb, var(--theme-ink) 88%, var(--theme-ground));
  --ink-85: color-mix(in srgb, var(--theme-ink) 85%, var(--theme-ground));
  --ink-82: color-mix(in srgb, var(--theme-ink) 82%, var(--theme-ground));
  --ink-78: color-mix(in srgb, var(--theme-ink) 78%, var(--theme-ground));
  --ink-72: color-mix(in srgb, var(--theme-ink) 72%, var(--theme-ground));
  --ink-70: color-mix(in srgb, var(--theme-ink) 70%, var(--theme-ground));
  --ink-62: color-mix(in srgb, var(--theme-ink) 62%, var(--theme-ground));
  --ink-60: color-mix(in srgb, var(--theme-ink) 60%, var(--theme-ground));
  --ink-55: color-mix(in srgb, var(--theme-ink) 55%, var(--theme-ground));
  --ink-50: color-mix(in srgb, var(--theme-ink) 50%, var(--theme-ground));
  --ink-45: color-mix(in srgb, var(--theme-ink) 45%, var(--theme-ground));
  --ink-40: color-mix(in srgb, var(--theme-ink) 40%, var(--theme-ground));
  --panel:  color-mix(in srgb, var(--ground) 55%, #ffffff);
  --panel-2:color-mix(in srgb, var(--ground) 62%, #ffffff);
  --paper:  color-mix(in srgb, var(--ground) 40%, #ffffff);
  /* the one alive element: theme-dark on cream */
  --live: var(--theme-dark);
  --font:"Figtree",-apple-system,"Helvetica Neue",Arial,sans-serif;
  --mono:var(--font);
}
.concepts-proof *{box-sizing:border-box;}
.concepts-proof{margin:0;background:var(--ground);color:var(--ink);
  font-family:var(--font);font-weight:400;line-height:1.5;min-height:100vh;
  background-image:radial-gradient(var(--dot) 1px, transparent 1.6px);
  background-size:24px 24px;
  transition:background-color .4s ease, color .4s ease;}
.concepts-proof [data-ref]{position:relative;}
.concepts-proof .wrap{max-width:1180px;margin:0 auto;padding:0 40px 96px;}

/* dark chips flip the alive element to accent so contrast holds */
.concepts-proof .chip.solid{--live:var(--theme-accent);}

/* ---- shared editorial frame (matches the other boards) ---- */
.concepts-proof h1,.concepts-proof h2,.concepts-proof h3{font-weight:600;letter-spacing:-.015em;line-height:1.04;margin:0;}
.concepts-proof section{margin-top:78px;}
.concepts-proof .masthead{padding-top:80px;}
.concepts-proof .eyebrow{text-transform:uppercase;letter-spacing:.24em;font-size:12.5px;font-weight:400;color:var(--dark);margin-bottom:26px;}
.concepts-proof .masthead h1{font-size:74px;letter-spacing:-.028em;margin-bottom:22px;}
.concepts-proof .masthead .intro{font-size:19px;max-width:680px;color:var(--ink-82);line-height:1.55;}
.concepts-proof .hr{height:1px;background:var(--hair);border:0;margin:34px 0 0;}
.concepts-proof .marker{text-transform:uppercase;font-weight:400;letter-spacing:.16em;font-size:12.5px;color:var(--ink-72);display:inline-block;margin-bottom:22px;}
.concepts-proof .marker b{font-weight:600;color:var(--ink);}
.concepts-proof .sec-h{font-size:30px;margin-bottom:6px;}
.concepts-proof .sec-p{max-width:640px;color:var(--ink-72);font-size:15px;margin:10px 0 0;}
.concepts-proof .foot{margin-top:90px;border-top:1px solid var(--hair);padding-top:26px;text-transform:uppercase;letter-spacing:.2em;font-size:12px;color:var(--ink-60);display:flex;justify-content:space-between;gap:20px;flex-wrap:wrap;}
.concepts-proof .foot .dot{color:var(--dark);}
.concepts-proof .panel{position:relative;background:var(--panel);border:1px solid var(--hair);border-radius:3px;padding:36px 40px 40px;margin-top:26px;}
.concepts-proof .panel.accent-bar::before{content:"";position:absolute;left:-1px;right:-1px;top:-1px;height:4px;background:var(--dark);border-radius:3px 3px 0 0;}

/* ---- shared linework (currentColor = ink context) ---- */
.concepts-proof .pf-l{fill:none;stroke:currentColor;stroke-opacity:.5;stroke-width:1;stroke-linecap:round;}
.concepts-proof .pf-l.soft{stroke-opacity:.26;}
.concepts-proof .pf-l.faint{stroke-opacity:.13;}
.concepts-proof .pf-l.strong{stroke-opacity:.72;stroke-width:1.2;}
.concepts-proof .pf-dash{stroke-dasharray:4 5;}
.concepts-proof .pf-dot{fill:currentColor;fill-opacity:.55;}
.concepts-proof .pf-core{fill:var(--live);}
.concepts-proof .pf-halo{fill:none;stroke:var(--live);stroke-opacity:.5;stroke-width:1.2;}
.concepts-proof .pf-gauge{fill:none;stroke:var(--live);stroke-width:6;stroke-linecap:round;}

/* ---- shared chips ---- */
.concepts-proof .chip{display:inline-flex;align-items:center;gap:7px;border:1px solid var(--hair);border-radius:5px;padding:5px 12px;text-transform:uppercase;letter-spacing:.14em;font-size:10px;color:var(--ink-70);white-space:nowrap;line-height:1;}
.concepts-proof .chip svg{width:12px;height:12px;color:var(--dark);flex:0 0 auto;}
.concepts-proof .chip.solid{background:var(--dark);color:var(--accent);border-color:transparent;font-weight:600;}
.concepts-proof .chip.solid svg{color:var(--accent);}
.concepts-proof .chip.dashed{border-style:dashed;border-color:var(--ink-40);}
.concepts-proof .chip.dot::before{content:"";width:6px;height:6px;border-radius:50%;background:var(--dark);flex:0 0 auto;}
.concepts-proof .chip.solid.dot::before{background:var(--accent);}

/* ============ PRF-001 · THE RECONCILIATION PLATE ============ */
.concepts-proof .plate{position:relative;background:var(--paper);border:1px solid var(--hair);border-radius:4px;
  padding:0;margin-top:26px;overflow:hidden;
  box-shadow:0 1px 0 var(--hair), 0 30px 60px -46px color-mix(in srgb, var(--ink) 44%, transparent);}
.concepts-proof .plate-top{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;
  padding:26px 34px 22px;border-bottom:1px solid var(--hair);
  background:color-mix(in srgb, var(--ground) 30%, #ffffff);}
.concepts-proof .pt-block{display:grid;grid-template-columns:max-content 1fr;gap:2px 20px;align-items:baseline;}
.concepts-proof .pt-row{display:contents;}
.concepts-proof .pt-k{text-transform:uppercase;letter-spacing:.22em;font-size:10px;color:var(--ink-55);padding:5px 0;}
.concepts-proof .pt-v{text-transform:uppercase;letter-spacing:.14em;font-size:12px;color:var(--ink-85);padding:5px 0;border-bottom:1px dotted var(--hair);}
.concepts-proof .pt-v.verified{color:var(--dark);font-weight:600;display:inline-flex;align-items:center;gap:7px;border-bottom:0;}
.concepts-proof .pt-v.verified svg{width:13px;height:13px;}
.concepts-proof .pt-seal{flex:0 0 auto;position:relative;width:118px;height:118px;display:grid;place-items:center;color:var(--ink);}
.concepts-proof .pt-seal svg{position:absolute;inset:0;width:118px;height:118px;}
.concepts-proof .pt-seal .seal-in{position:relative;display:flex;flex-direction:column;align-items:center;gap:4px;color:var(--dark);}
.concepts-proof .pt-seal .seal-in svg{position:static;width:16px;height:16px;}
.concepts-proof .pt-seal .seal-txt{text-transform:uppercase;letter-spacing:.2em;font-size:9px;font-weight:600;text-align:center;line-height:1.4;color:var(--dark);}

.concepts-proof .ledger{padding:8px 34px 4px;}
.concepts-proof .lgrid{display:grid;grid-template-columns:1.7fr 1fr 46px 1fr 1.2fr;align-items:center;}
.concepts-proof .ledger-head{border-bottom:1px solid var(--hair);padding:14px 0 12px;}
.concepts-proof .ledger-head .lh{text-transform:uppercase;letter-spacing:.16em;font-size:10px;color:var(--ink-55);}
.concepts-proof .ledger-head .lh.r{text-align:right;}
.concepts-proof .ledger-head .lh.model{color:var(--ink-70);}
.concepts-proof .ledger-head .lh.delta{text-align:right;color:var(--dark);}
.concepts-proof .lrow{padding:13px 0;border-bottom:1px solid var(--hair-soft);}
.concepts-proof .lrow:last-child{border-bottom:0;}
.concepts-proof .l-item{font-size:14px;color:var(--ink-78);letter-spacing:-.005em;}
.concepts-proof .l-val{text-align:right;font-size:14px;color:var(--ink-82);font-variant-numeric:tabular-nums;font-feature-settings:"tnum" 1;letter-spacing:.01em;}
.concepts-proof .l-lead{height:1px;margin:0 8px;border-top:1px dotted var(--ink-40);position:relative;}
.concepts-proof .l-lead::before,.concepts-proof .l-lead::after{content:"";position:absolute;top:50%;width:3px;height:3px;border-radius:50%;background:var(--ink-45);transform:translateY(-50%);}
.concepts-proof .l-lead::before{left:-1px;}
.concepts-proof .l-lead::after{right:-1px;}
.concepts-proof .l-delta{display:flex;align-items:center;justify-content:flex-end;gap:9px;}
.concepts-proof .l-delta .dz{font-size:13px;color:var(--ink-62);font-variant-numeric:tabular-nums;letter-spacing:.02em;}
.concepts-proof .l-delta .dtick{width:17px;height:17px;flex:0 0 auto;display:grid;place-items:center;border-radius:50%;
  border:1px solid color-mix(in srgb, var(--dark) 40%, transparent);color:var(--dark);
  opacity:0;transform:scale(.55);
  animation:pf-tick .46s cubic-bezier(.2,.9,.3,1.2) forwards;
  animation-delay:calc(var(--i, 0) * .11s + .25s);}
.concepts-proof .l-delta .dtick svg{width:10px;height:10px;display:block;}
@keyframes pf-tick{to{opacity:1;transform:scale(1);}}
/* subtotal rows: heavier rule + inked figures */
.concepts-proof .lrow[data-kind="subtotal"]{border-top:1px solid var(--hair);margin-top:-1px;}
.concepts-proof .lrow[data-kind="subtotal"] .l-item{color:var(--ink);font-weight:600;font-size:14px;}
.concepts-proof .lrow[data-kind="subtotal"] .l-val{color:var(--ink);font-weight:600;}
.concepts-proof .lrow[data-kind="net"]{border-top:2px solid var(--ink-40);}
.concepts-proof .lrow[data-kind="net"] .l-item{color:var(--ink);font-weight:600;}
.concepts-proof .lrow[data-kind="net"] .l-val{color:var(--ink);font-weight:600;}
.concepts-proof .ledger-foot{display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap;
  padding:16px 34px 22px;border-top:1px solid var(--hair);margin-top:2px;
  background:color-mix(in srgb, var(--ground) 30%, #ffffff);
  text-transform:uppercase;letter-spacing:.16em;font-size:10.5px;color:var(--ink-60);}
.concepts-proof .ledger-foot b{color:var(--dark);font-weight:600;}

/* ============ PRF-002 · THE EVALUATION SCOREBOARD ============ */
.concepts-proof .board-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;flex-wrap:wrap;gap:12px;}
.concepts-proof .board-title{text-transform:uppercase;letter-spacing:.2em;font-size:12px;color:var(--ink-55);}
.concepts-proof .sgrid{display:grid;grid-template-columns:1.7fr .7fr .7fr 78px 1fr;align-items:center;gap:0;}
.concepts-proof .shead-row{border-bottom:1px solid var(--hair);padding-bottom:12px;}
.concepts-proof .shead{text-transform:uppercase;letter-spacing:.16em;font-size:10px;color:var(--ink-55);}
.concepts-proof .shead.r{text-align:right;}
.concepts-proof .shead.c{text-align:center;}
.concepts-proof .srow{border-bottom:1px solid var(--hair-soft);}
.concepts-proof .srow > *{padding:15px 0;}
.concepts-proof .srow:last-child{border-bottom:0;}
.concepts-proof .s-suite{display:flex;align-items:center;gap:11px;font-size:14px;color:var(--ink-82);}
.concepts-proof .s-suite .sdot{width:7px;height:7px;border-radius:50%;background:var(--ink-40);flex:0 0 auto;}
.concepts-proof .srow.newest .s-suite .sdot{background:var(--dark);box-shadow:0 0 0 3px color-mix(in srgb, var(--dark) 16%, transparent);}
.concepts-proof .srow.newest .s-suite b{font-weight:600;color:var(--ink);}
.concepts-proof .s-new{margin-left:2px;}
.concepts-proof .s-num{text-align:right;font-size:14px;color:var(--ink-78);font-variant-numeric:tabular-nums;padding-right:14px;}
.concepts-proof .s-num .of{color:var(--ink-50);}
.concepts-proof .s-cov{display:flex;justify-content:center;}
.concepts-proof .s-status{display:flex;justify-content:flex-end;}
.concepts-proof .board-foot{margin-top:22px;padding-top:16px;border-top:1px solid var(--hair);
  display:flex;align-items:center;gap:10px;flex-wrap:wrap;
  text-transform:uppercase;letter-spacing:.18em;font-size:10.5px;color:var(--ink-60);}
.concepts-proof .board-foot .pin{color:var(--dark);font-weight:600;display:inline-flex;align-items:center;gap:7px;}
.concepts-proof .board-foot .pin svg{width:12px;height:12px;}
.concepts-proof .board-foot .sep{color:var(--ink-40);}
.concepts-proof .board-foot .sha{color:var(--ink-72);letter-spacing:.1em;}

/* coverage arc meter (computed path) */
.concepts-proof .cov-meter{position:relative;display:inline-grid;place-items:center;color:var(--ink);}
.concepts-proof .cov-meter svg{display:block;width:100%;height:100%;}
.concepts-proof .cov-num{position:absolute;font-size:11px;letter-spacing:.02em;color:var(--ink-70);font-variant-numeric:tabular-nums;}
.concepts-proof .cov-num b{color:var(--ink);font-weight:600;}

/* ============ PRF-003 · THE BLIND TEST ============ */
.concepts-proof .triptych{display:grid;grid-template-columns:1fr 1fr 1fr;gap:0;margin-top:6px;
  border:1px solid var(--hair);border-radius:8px;overflow:hidden;background:var(--panel-2);}
.concepts-proof .tpanel{position:relative;padding:30px 26px 28px;min-height:250px;
  display:flex;flex-direction:column;align-items:center;text-align:center;gap:14px;
  border-right:1px solid var(--hair);}
.concepts-proof .tpanel:last-child{border-right:0;}
.concepts-proof .tpanel.sealed{background:
  repeating-linear-gradient(45deg, transparent 0 9px, color-mix(in srgb, var(--ink) 4%, transparent) 9px 10px), var(--panel-2);}
.concepts-proof .tstep{text-transform:uppercase;letter-spacing:.24em;font-size:10px;color:var(--dark);}
.concepts-proof .tstep .n{color:var(--ink-45);margin-right:8px;}
.concepts-proof .tstage{flex:1;display:flex;align-items:center;justify-content:center;width:100%;min-height:96px;}
.concepts-proof .tstage svg{width:118px;height:auto;color:var(--ink);}
.concepts-proof .ttitle{font-size:15px;font-weight:600;color:var(--ink-85);letter-spacing:-.01em;}
.concepts-proof .tnote{font-size:12px;color:var(--ink-60);max-width:200px;line-height:1.5;}

/* sealed panel — dashed boundary card inside */
.concepts-proof .sealed-card{position:relative;width:132px;height:96px;border:1px dashed var(--ink-40);border-radius:5px;
  display:grid;place-items:center;background:color-mix(in srgb, var(--ground) 46%, #ffffff);}
.concepts-proof .sealed-card svg{width:88px;height:auto;}

/* run panel — twin mark + connector to unknown source */
.concepts-proof .run-line{display:flex;align-items:center;justify-content:center;gap:0;width:100%;}
.concepts-proof .run-mark{width:70px;height:70px;color:var(--ink);flex:0 0 auto;}
.concepts-proof .run-mark svg{width:70px;height:70px;}
.concepts-proof .run-conn{flex:1;max-width:60px;height:1px;position:relative;
  background:repeating-linear-gradient(90deg, var(--live) 0 4px, transparent 4px 9px);}
.concepts-proof .run-conn::before,.concepts-proof .run-conn::after{content:"";position:absolute;top:50%;width:5px;height:5px;border-radius:50%;background:var(--live);transform:translateY(-50%);}
.concepts-proof .run-conn::before{left:-2px;}
.concepts-proof .run-conn::after{right:-2px;}
.concepts-proof .run-ghost{flex:0 0 auto;width:70px;height:70px;border-radius:12px;border:1px dashed var(--ink-45);
  display:grid;place-items:center;position:relative;overflow:hidden;
  background:color-mix(in srgb, var(--ground) 52%, #ffffff);}
.concepts-proof .run-ghost::before{content:"";position:absolute;inset:0;
  background-image:repeating-linear-gradient(45deg, color-mix(in srgb, var(--ink) 7%, transparent) 0 5px, transparent 5px 10px);}
.concepts-proof .run-ghost .q{position:relative;font-size:28px;font-weight:600;color:var(--ink-55);line-height:1;}

/* scored panel — assessor certificate fragment */
.concepts-proof .scored-cert{width:100%;max-width:190px;border:1px solid var(--ink-40);border-radius:4px;
  padding:16px 16px 14px;text-align:left;background:color-mix(in srgb, var(--ground) 44%, #ffffff);position:relative;}
.concepts-proof .scored-cert .sc-eye{text-transform:uppercase;letter-spacing:.2em;font-size:8.5px;color:var(--ink-55);}
.concepts-proof .scored-cert .sc-scoreline{display:flex;justify-content:space-between;align-items:baseline;margin-top:12px;padding-bottom:6px;border-bottom:1px solid var(--hair);}
.concepts-proof .scored-cert .sc-k{text-transform:uppercase;letter-spacing:.14em;font-size:8.5px;color:var(--ink-55);}
.concepts-proof .scored-cert .sc-empty{font-size:16px;letter-spacing:.26em;color:var(--ink-45);font-weight:600;}
.concepts-proof .scored-cert .sc-sig{margin-top:14px;}
.concepts-proof .scored-cert .sc-sigline{height:1px;background:var(--ink-40);}
.concepts-proof .scored-cert .sc-sigk{text-transform:uppercase;letter-spacing:.14em;font-size:8px;color:var(--ink-55);margin-top:5px;}
.concepts-proof .trip-strip{display:flex;align-items:center;gap:14px;flex-wrap:wrap;margin-top:18px;
  padding-top:16px;border-top:1px solid var(--hair);}
.concepts-proof .trip-strip .ts-lbl{text-transform:uppercase;letter-spacing:.18em;font-size:10.5px;color:var(--ink-60);}

/* ============ PRF-004 · LIVE TODAY ============ */
.concepts-proof .estates{display:grid;grid-template-columns:1fr 1fr;gap:22px;margin-top:6px;}
.concepts-proof .estate{position:relative;border:1px solid var(--hair);border-radius:8px;padding:26px 28px 24px;background:var(--panel-2);}
.concepts-proof .estate-top{display:flex;align-items:center;justify-content:space-between;gap:14px;margin-bottom:22px;}
.concepts-proof .estate-src{display:flex;align-items:center;gap:12px;}
.concepts-proof .estate-src img{width:34px;height:34px;object-fit:contain;display:block;}
.concepts-proof .estate-src img.brand-svg{width:30px;height:30px;}
.concepts-proof .estate-src .es-name{}
.concepts-proof .estate-src .es-name b{display:block;font-size:15px;font-weight:600;letter-spacing:-.01em;color:var(--ink);}
.concepts-proof .estate-src .es-name span{text-transform:uppercase;letter-spacing:.16em;font-size:9.5px;color:var(--ink-55);}
.concepts-proof .estate-body{display:flex;align-items:center;gap:22px;}
.concepts-proof .estate-cov{flex:0 0 auto;}
.concepts-proof .estate-meta{flex:1;min-width:0;}
.concepts-proof .estate-meta .live-row{display:flex;align-items:center;gap:9px;margin-bottom:12px;}
.concepts-proof .estate-meta .live-dot{width:7px;height:7px;border-radius:50%;background:var(--dark);flex:0 0 auto;
  box-shadow:0 0 0 0 color-mix(in srgb, var(--dark) 45%, transparent);animation:pf-pulse 2.6s ease-out infinite;}
@keyframes pf-pulse{0%{box-shadow:0 0 0 0 color-mix(in srgb, var(--dark) 42%, transparent);}70%{box-shadow:0 0 0 7px color-mix(in srgb, var(--dark) 0%, transparent);}100%{box-shadow:0 0 0 0 color-mix(in srgb, var(--dark) 0%, transparent);}}
.concepts-proof .estate-meta .live-lbl{text-transform:uppercase;letter-spacing:.18em;font-size:10px;color:var(--dark);font-weight:600;}
.concepts-proof .estate-metric{font-size:13px;color:var(--ink-72);line-height:1.5;}
.concepts-proof .estate-metric b{color:var(--ink);font-weight:600;font-variant-numeric:tabular-nums;}
.concepts-proof .estate-metric .rel{display:inline-flex;align-items:center;gap:5px;border:1px solid var(--hair);border-radius:5px;padding:0 7px;font-size:12px;color:var(--ink-78);}
.concepts-proof .estate-metric .rel .arrow{color:var(--dark);}
.concepts-proof .estate-foot{display:flex;align-items:center;gap:10px;margin-top:18px;padding-top:15px;border-top:1px solid var(--hair-soft);flex-wrap:wrap;}
.concepts-proof .live-cap{margin-top:20px;text-align:center;text-transform:uppercase;letter-spacing:.2em;font-size:11px;color:var(--ink-60);}
.concepts-proof .live-cap b{color:var(--dark);font-weight:600;}

/* ============ PRF-005 · WHY ARTEFACTS ============ */
.concepts-proof .why{border-left:3px solid var(--dark);padding:6px 0 6px 26px;max-width:660px;margin-top:8px;}
.concepts-proof .why .why-eye{text-transform:uppercase;letter-spacing:.22em;font-size:11px;color:var(--dark);margin-bottom:14px;}
.concepts-proof .why p{font-size:18px;line-height:1.62;color:var(--ink-85);margin:0;letter-spacing:-.008em;}
.concepts-proof .why p em{font-style:italic;color:var(--ink);}

/* ---- responsive ---- */
@media (max-width:900px){
  .concepts-proof .wrap{padding:0 22px 72px;}
  .concepts-proof .masthead h1{font-size:46px;}
  .concepts-proof .plate-top{flex-direction:column-reverse;align-items:stretch;gap:18px;}
  .concepts-proof .pt-seal{align-self:flex-end;}
  .concepts-proof .ledger{padding:8px 20px 4px;}
  .concepts-proof .plate-top,.concepts-proof .ledger-foot{padding-left:20px;padding-right:20px;}
  .concepts-proof .lgrid{grid-template-columns:1.5fr 1fr 1fr;}
  .concepts-proof .ledger-head .lh.truth-h,.concepts-proof .ledger-head .lh.lead-h,.concepts-proof .l-lead,.concepts-proof .lrow .l-val.truth{display:none;}
  .concepts-proof .ledger-head .lh.model{text-align:right;}
  .concepts-proof .sgrid{grid-template-columns:1.6fr .6fr 72px auto;column-gap:14px;}
  .concepts-proof .shead.pass-h,.concepts-proof .s-num.pass-c{display:none;}
  .concepts-proof .s-num{padding-right:0;}
  .concepts-proof .triptych{grid-template-columns:1fr;}
  .concepts-proof .tpanel{border-right:0;border-bottom:1px solid var(--hair);}
  .concepts-proof .tpanel:last-child{border-bottom:0;}
  .concepts-proof .estates{grid-template-columns:1fr;}
}
@media (max-width:560px){
  .concepts-proof .shead-row{display:none;}
  .concepts-proof .sgrid{grid-template-columns:1.4fr .5fr 50px auto;column-gap:9px;}
  .concepts-proof .s-suite{font-size:13px;gap:8px;}
  .concepts-proof .s-suite .chip.s-new{display:none;}
  .concepts-proof .s-num{font-size:13px;}
  .concepts-proof .s-status .chip{padding:5px 9px;letter-spacing:.08em;gap:5px;}
  .concepts-proof .board-head{margin-bottom:14px;}
  .concepts-proof .board-foot{font-size:9.5px;letter-spacing:.12em;}
}
@media (prefers-reduced-motion:reduce){
  .concepts-proof .l-delta .dtick{animation:none;opacity:1;transform:none;}
  .concepts-proof .estate-meta .live-dot{animation:none;}
  .concepts-proof{transition:none;}
}`;

export const E: Record<string, EngravingData> = {
  // ---- PRF-001 · "to the penny" seal — concentric rings + dashed chapter ring ----
  pennySeal: {
    viewBox: "0 0 120 120",
    role: "img",
    ariaLabel: "Reconciliation seal",
    inner: `
      <circle class="pf-l" cx="60" cy="60" r="54"/>
      <circle class="pf-l soft cx-dash" cx="60" cy="60" r="47" style="stroke-dasharray:1.5 5.5;"/>
      <circle class="pf-l soft" cx="60" cy="60" r="40"/>
      <path class="pf-l faint" d="M60 20 V6 M60 114 V100 M20 60 H6 M114 60 H100"/>
    `,
  },

  // ---- reusable clean twin mark (orbit + globe + one live station) ----
  twinMark: {
    viewBox: "0 0 120 120",
    role: "img",
    ariaLabel: "The twin mark — orbit, globe and a live station",
    inner: `
      <ellipse class="pf-l soft" cx="60" cy="60" rx="50" ry="15"/>
      <circle class="pf-l" cx="60" cy="60" r="34"/>
      <ellipse class="pf-l soft" cx="60" cy="60" rx="34" ry="9"/>
      <ellipse class="pf-l faint" cx="60" cy="60" rx="13" ry="34"/>
      <line class="pf-l faint" x1="60" y1="26" x2="60" y2="94"/>
      <circle class="pf-halo" cx="102.4" cy="52.05" r="6"/>
      <circle class="pf-core" cx="102.4" cy="52.05" r="3.4"/>
      <circle class="pf-core" cx="60" cy="60" r="4.4"/>
    `,
  },

  // ---- sealed envelope — the business the twin has never met ----
  envelope: {
    viewBox: "0 0 200 128",
    role: "img",
    ariaLabel: "A sealed envelope — a business held out of the twin",
    inner: `
      <rect class="pf-l" x="26" y="26" width="148" height="88" rx="4"/>
      <path class="pf-l soft" d="M28 30 L100 82 L172 30"/>
      <path class="pf-l faint" d="M28 110 L74 74 M172 110 L126 74"/>
      <circle class="pf-halo" cx="100" cy="82" r="12"/>
      <circle class="pf-core" cx="100" cy="82" r="5"/>
    `,
  },
};
